import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Truck, Headphones, ShoppingBag, Server, Activity, Cpu, Package } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const features = [
  { icon: <Truck size={24} strokeWidth={1.5} />, title: 'Free Setup', desc: 'Complimentary installation and onboarding.' },
  { icon: <Headphones size={24} strokeWidth={1.5} />, title: 'Specialist Support', desc: 'Personalized guidance from FOZZIL experts.' },
  { icon: <ShoppingBag size={24} strokeWidth={1.5} />, title: 'Enterprise Licensing', desc: 'Flexible plans for institutions.' },
];

const StoreLanding = () => {
  const navigate = useNavigate();

  const productCategories = [
    { id: 'ark', name: 'ARK', tagline: 'AI Museum', image: process.env.PUBLIC_URL + '/ark-product.png', path: '/shop/buy-ark' },
    { id: 'medos', name: 'MedOS', tagline: 'Clinical Software', image: process.env.PUBLIC_URL + '/FOZZIL MEDOS LOGO.png', path: '/shop/medos' },
    { id: 'intelligence', name: 'Intelligence', tagline: 'AI Platform', image: process.env.PUBLIC_URL + '/logoicon.png', path: '/shop/intelligence' },
  ];

  const productCards = [
    {
      id: 'ark-hero', badge: 'HARDWARE', name: 'FOZZIL ARK', tagline: 'Intelligence. Contained.',
      description: 'Plug-and-play AI Museum with T.A.E.L.S Core, S.H.A.D.E. Vision, and Living Ancestor Engine.',
      logo: process.env.PUBLIC_URL + '/ark-product.png', accentColor: '#065DC0', ctaLabel: 'Shop ARK', ctaPath: '/shop/buy-ark', learnPath: '/fozzil-ark',
    },
    {
      id: 'medos-hero', badge: 'SOFTWARE', name: 'FOZZIL MedOS', tagline: 'Clinical infrastructure, reimagined.',
      description: 'AI-driven pharmacy and hospital management with GST-ready invoicing, smart inventory, and ZYPHER AI.',
      logo: process.env.PUBLIC_URL + '/FOZZIL MEDOS LOGO.png', accentColor: '#065DC0', ctaLabel: 'Shop MedOS', ctaPath: '/shop/medos', learnPath: '/products/medos',
    },
    {
      id: 'intelligence-hero', badge: 'SUBSCRIPTION', name: 'T.A.E.L.S', tagline: 'The evolution of AI.',
      description: 'Multi-modal AI with T.A.E.L.S APEX Thinking Engines, Deep Research, Stealth Mode, and advanced creative suite.',
      logo: process.env.PUBLIC_URL + '/TAELS Logo black bg.png', accentColor: '#065DC0', ctaLabel: 'Subscribe', ctaPath: '/shop/intelligence', learnPath: '/taels',
    },
  ];

  return (
    <div className="store-page bg-white dark:bg-black text-[#1d1d1f] dark:text-[#f5f5f7] transition-colors duration-400 min-h-screen">
      {/* Hero */}
      <section className="py-8 sm:py-12 md:py-20">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 sm:gap-6">
            <div>
              <motion.h1 variants={fadeInUp} className="text-[clamp(36px,7vw,80px)] font-bold tracking-tight leading-none">Store</motion.h1>
              <motion.p variants={fadeInUp} className="text-[#6e6e73] dark:text-[#a1a1a6] text-base sm:text-lg md:text-xl font-light mt-2 sm:mt-3 max-w-lg">The best way to buy the products you love.</motion.p>
            </div>            
          </motion.div>
        </div>
      </section>

      {/* Category Quick Nav */}
      <section className="pb-8 sm:pb-10 md:pb-14">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="flex justify-center gap-4 sm:gap-6 md:gap-12 flex-wrap">
            {productCategories.map((cat) => (
              <motion.button key={cat.id} variants={fadeInUp} onClick={() => navigate(cat.path)} className="flex flex-col items-center gap-2 sm:gap-3 group cursor-pointer bg-transparent border-none p-0 relative">
                <div className="absolute inset-0 bg-blue-500/10 dark:bg-blue-400/5 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="w-[70px] h-[70px] sm:w-[90px] sm:h-[90px] md:w-[120px] md:h-[120px] rounded-[20px] sm:rounded-[28px] md:rounded-[32px] bg-white dark:bg-[#050505] border border-black/[0.06] dark:border-white/[0.1] flex items-center justify-center overflow-hidden transition-all duration-500 group-hover:shadow-[0_8px_30px_rgba(6,93,192,0.15)] group-hover:scale-105 group-hover:border-blue-500/50 relative z-10">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-contain" />
                </div>
                <div className="text-center relative z-10">
                  <p className="text-xs sm:text-sm font-semibold text-[#1d1d1f] dark:text-zinc-200 group-hover:text-[#065DC0] dark:group-hover:text-blue-400 transition-colors">{cat.name}</p>
                  <p className="text-[10px] sm:text-[11px] text-[#86868b] dark:text-[#6e6e73] font-medium">{cat.tagline}</p>
                </div>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6"><div className="h-px bg-black/[0.08] dark:bg-white/[0.06]" /></div>

      {/* The Latest - Ecosystem Card Style */}
      <section className="py-8 sm:py-12 md:py-16">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="text-[clamp(20px,3.5vw,36px)] font-bold tracking-tight mb-1 sm:mb-2">
              The latest. <span className="text-[#065DC0]/80 dark:text-blue-400/80 font-medium">Take a look at what's new, right now.</span>
            </motion.h2>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 mt-6 sm:mt-8">
              {productCards.map((p) => {
                return (
                  <motion.div key={p.id} variants={fadeInUp}>
                    <div
                      onClick={() => navigate(p.ctaPath)}
                      className="relative group p-4 sm:p-5 md:p-6 lg:p-7 rounded-[20px] sm:rounded-[24px] md:rounded-[32px] bg-white dark:bg-zinc-950 border border-black/[0.08] dark:border-white/[0.08] hover:border-blue-500/50 shadow-sm dark:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.5)] transition-all duration-700 overflow-hidden h-full flex flex-col cursor-pointer"
                    >
                      {/* Artistic Background Elements */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.03] to-transparent pointer-events-none" />
                      <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#065DC0]/5 blur-[80px] rounded-full group-hover:bg-[#065DC0]/10 transition-colors duration-700 pointer-events-none" />
                      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-400/5 blur-[80px] rounded-full group-hover:bg-blue-400/8 transition-colors duration-700 pointer-events-none" />
                      
                      {/* Hover Glow - Grok Style */}
                      <div className="absolute top-0 right-0 w-32 sm:w-48 h-32 sm:h-48 blur-[60px] md:blur-[80px] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ backgroundColor: p.accentColor }} />

                      <span className="text-[9px] sm:text-[10px] md:text-[11px] font-bold uppercase tracking-[0.15em] mb-2 md:mb-4 relative z-10" style={{ color: p.accentColor }}>{p.badge}</span>

                      {/* Logo Container */}
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-[32px] ${p.id === 'ark-hero' ? 'bg-white dark:bg-black' : 'bg-black'} flex items-center justify-center mb-3 md:mb-5 group-hover:scale-110 transition-transform duration-700 border border-black/[0.06] dark:border-white/[0.1] relative z-10 overflow-hidden p-0`}>
                        <div className="absolute inset-0 bg-blue-500/5 dark:bg-blue-400/10 pointer-events-none" />
                        <img src={p.logo} alt={p.name} className="w-full h-full object-contain drop-shadow-md" />
                      </div>

                      <h3 className="text-sm sm:text-base md:text-xl lg:text-2xl font-bold text-[#1d1d1f] dark:text-white mb-0.5 sm:mb-1 tracking-tight transition-colors uppercase relative z-10">{p.name}</h3>
                      <p className="text-[10px] sm:text-[11px] md:text-xs lg:text-sm font-light text-[#6e6e73] dark:text-zinc-400 mb-2 md:mb-4 transition-colors leading-relaxed line-clamp-3 sm:line-clamp-none relative z-10">{p.description}</p>

                      {/* CTA - Single Line */}
                      <div className="flex items-center gap-1.5 sm:gap-2 mt-auto pt-3 md:pt-4 border-t border-black/[0.06] dark:border-white/10 w-full relative z-10">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(p.ctaPath); }}
                          className="flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full text-white font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all border-none cursor-pointer flex items-center justify-center hover:opacity-90 shadow-[0_4px_14px_rgba(6,93,192,0.4)]"
                          style={{ backgroundColor: p.accentColor }}
                        >
                          {p.ctaLabel}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); window.open(p.learnPath, '_blank'); }}
                          className="flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-transparent font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all border cursor-pointer flex items-center justify-center dark:hover:text-white"
                          style={{ color: p.accentColor, borderColor: p.accentColor }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = p.accentColor; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = p.accentColor; }}
                        >
                          Learn more
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Help Strip */}
      <section className="py-6 sm:py-8 md:py-14">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="text-[clamp(20px,3.5vw,36px)] font-bold tracking-tight mb-1 sm:mb-2">
              The FOZZIL Store difference. <span className="text-[#065DC0]/80 dark:text-blue-400/80 font-medium">Even more reasons to shop with us.</span>
            </motion.h2>
          </motion.div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="pb-10 sm:pb-12 md:pb-16">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger} className="grid grid-cols-2 sm:grid-cols-3 gap-3.5 sm:gap-6 md:gap-8">
            {features.map((f, i) => (
              <motion.div key={i} variants={fadeInUp} className={`text-center px-3 sm:px-4 py-4 sm:py-6 rounded-[20px] sm:rounded-[28px] md:rounded-[32px] bg-white dark:bg-[#050505] border border-black/[0.06] dark:border-white/[0.08] shadow-sm hover:border-blue-500/30 transition-colors duration-500 ${
                i === 2 ? "col-span-2 sm:col-span-1" : ""
              }`}>
                <div className="text-[#065DC0] dark:text-blue-400 mb-3 flex justify-center drop-shadow-sm">{f.icon}</div>
                <h3 className="text-sm sm:text-base md:text-[17px] font-bold text-[#1d1d1f] dark:text-white mb-1 sm:mb-2">{f.title}</h3>
                <p className="text-xs sm:text-sm text-[#6e6e73] dark:text-zinc-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-6 sm:py-8 md:py-14 border-t border-black/[0.06] dark:border-white/[0.06]">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {[
              { icon: <img src={process.env.PUBLIC_URL + '/ark-product.png'} alt="" className="w-[18px] h-[18px] object-contain" />, title: 'Shop Ark', desc: 'AI Museum', path: '/shop/buy-ark' },
              { icon: <Activity size={18} />, title: 'Shop MedOS', desc: 'Clinical Software', path: '/shop/medos' },
              { icon: <Cpu size={18} />, title: 'Subscribe Intelligence', desc: 'AI Platform', path: '/shop/intelligence' },
              { icon: <Package size={18} />, title: 'Your Orders', desc: 'Track & manage', path: '/shop/orders' },
            ].map((link, i) => (
              <motion.div key={i} variants={fadeInUp}>
                <Link to={link.path} className="flex items-center gap-2 sm:gap-3 md:gap-4 p-3 sm:p-4 md:p-5 rounded-[16px] sm:rounded-[20px] bg-white dark:bg-[#050505] border border-black/[0.06] dark:border-white/[0.08] hover:border-[#065DC0]/50 dark:hover:border-blue-500/50 shadow-sm transition-all duration-300 group">
                  <div className="text-[#065DC0] dark:text-blue-500 group-hover:scale-110 transition-transform shrink-0 drop-shadow-sm">{link.icon}</div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-[#1d1d1f] dark:text-zinc-200 group-hover:text-[#065DC0] dark:group-hover:text-blue-400 transition-colors truncate">{link.title}</p>
                    <p className="text-[10px] sm:text-xs text-[#86868b] dark:text-zinc-500 truncate">{link.desc}</p>
                  </div>
                  <ArrowRight size={12} className="ml-auto text-[#86868b] dark:text-zinc-600 group-hover:text-[#065DC0] dark:group-hover:text-blue-400 transition-colors shrink-0 hidden sm:block" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default StoreLanding;
